import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import { FloatingUIManager } from '../../utils/floating-ui'
import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'
type TooltipType = 'standard' | 'popover'
type TooltipDensity = 'large' | 'medium' | 'small'
type TooltipState = 'info' | 'warning' | 'error' | 'success'

/**
 * Para uma descrição detalhada, consulte a [documentação do GovBR-DS](https:
 *
 * @slot trigger - Área para o elemento acionador do tooltip.
 * @slot content - Área para o conteúdo do tooltip.
 */
@Component({
  tag: 'br-tooltip',
  styleUrl: 'tooltip.scss',
  shadow: true,
  formAssociated: true,
})
export class Tooltip {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrTooltipElement

  /**
   * Referência ao elemento Internals do componente.
   * O elemento Internals fornece uma interface para acessar e manipular o estado interno do componente.
   * Isso é útil para componentes que precisam se integrar com o sistema de formulários nativo do navegador.
   */
  @AttachInternals() elementInternals: ElementInternals

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) customId: string = Helpers.generateUniqueId()

  /**
   * Define a posição do tooltip em relação ao elemento acionador.
   * As opções disponíveis são: 'top', 'bottom', 'left' e 'right'.
   * @default 'top'
   */
  @Prop({ reflect: true }) position: TooltipPosition = 'top'

  /**
   * Define o tipo de tooltip a ser exibido.
   * As opções disponíveis são: 'standard' e 'popover'.
   * O tipo 'standard' exibe um tooltip simples, enquanto o tipo 'popover' exibe um tooltip com mais informações e fechamento por interação.
   * @default 'standard'
   */
  @Prop({ reflect: true }) type: TooltipType = 'standard'

  /**
   * Define a densidade do tooltip.
   * As opções disponíveis são: 'large', 'medium' e 'small'.
   * A densidade 'small' exibe um tooltip mais compacto, enquanto a densidade 'large' exibe um tooltip mais espaçado.
   * @default 'medium'
   */
  @Prop({ reflect: true }) density: TooltipDensity = 'medium'

  /**
   * Define o estado do tooltip.
   * Os estados disponíveis são: 'info', 'warning', 'error' e 'success'.
   * @default 'info'
   */
  @Prop({ reflect: true }) state: TooltipState = 'info'

  /**
   * Define se o tooltip deve ser exibido automaticamente.
   * Quando definido como true, o tooltip será exibido automaticamente.
   * Quando definido como false, o tooltip será exibido apenas quando acionado pelo usuário.
   * @default false
   */
  @Prop({ reflect: true }) isAutoVisible: boolean = false

  /**
   * Define o atraso em milissegundos para exibir o tooltip.
   * @default null
   */
  @Prop({ reflect: true }) showDelay: number = null

  /**
   * Define o atraso em milissegundos para ocultar o tooltip.
   * @default null
   */
  @Prop({ reflect: true }) hideDelay: number = null

  /**
   * Define a visibilidade do tooltip.
   * Quando definido como true, o tooltip será exibido.
   * Quando definido como false, o tooltip será ocultado.
   * @default false
   */
  @State() isVisible: boolean = false

  /**
   * Watcher para monitorar alterações na propriedade `isVisible`.
   * @param visible boolean
   * @returns void
   */
  @Watch('isVisible')
  handleVisibilityChange(visible: boolean) {
    if (this.contentElement == null) return

    if (visible) {
      this.positionTooltip()
      this.brDidOpen.emit()
    } else {
      this.clearAutoUpdate?.()
      this.clearAutoUpdate = undefined
      this.brDidClose.emit()
    }
  }

  /**
   * Evento emitido quando o tooltip é aberto.
   */
  @Event() brDidOpen: EventEmitter<void>

  /**
   * Evento emitido quando o tooltip é fechado.
   */
  @Event() brDidClose: EventEmitter<void>

  /**
   * Listener para o evento de foco no elemento acionador.
   * Este evento é utilizado para exibir o tooltip quando o elemento acionador recebe foco.
   */
  @Listen('focusin')
  handleFocusIn() {
    if (!this.fromPointerDown) {
      this.showContent()
    }
    this.fromPointerDown = false
  }

  /**
   * Listener para o evento de desfoque no elemento acionador.
   * Este evento é utilizado para ocultar o tooltip quando o elemento acionador perde o foco.
   */
  @Listen('focusout')
  handleFocusOut() {
    this.hideContent()
  }

  /**
   * Listener para o evento de teclado no documento.
   * Este evento é utilizado para fechar o tooltip quando a tecla Escape é pressionada.
   * @param event KeyboardEvent
   */
  @Listen('keydown', { target: 'document' })
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.hideContent()
      this.triggerElement?.focus()
    }
  }

  private floatingManager: FloatingUIManager

  /**
   * Elemento que aciona a exibição do tooltip.
   * Este elemento é referenciado para garantir que o tooltip seja posicionado corretamente em relação a ele.
   */
  private triggerElement: HTMLDivElement

  /**
   * Elemento que contém o conteúdo do tooltip.
   * Este elemento é referenciado para garantir que o tooltip seja posicionado corretamente em relação a ele.
   */
  private contentElement: HTMLDivElement

  /**
   * Elemento que representa a seta do tooltip.
   * Este elemento é utilizado para indicar a direção do tooltip em relação ao elemento acionador.
   */
  private arrowElement: HTMLDivElement

  /**
   * Função de limpeza para atualizar a posição do tooltip automaticamente.
   * Esta função é chamada quando o tooltip não está mais visível ou quando o componente é desconectado.
   * Isso garante que o tooltip não consuma recursos desnecessários quando não estiver em uso.
   */
  private clearAutoUpdate: () => void

  /**
   * Tempo limite para exibir o tooltip automaticamente.
   */
  private showTimer: ReturnType<typeof setTimeout>

  /**
   * Tempo limite para ocultar o tooltip.
   */
  private hideTimer: ReturnType<typeof setTimeout>

  /**
   * Indica se o tooltip foi acionado por um clique do mouse.
   * Isso é utilizado para evitar conflito com o acionamento por foco.
   */
  private fromPointerDown: boolean = false

  /**
   * Controla a exibição do tooltip.
   * Quando o tooltip é exibido, ele é posicionado em relação ao elemento acionador.
   * Isso é feito utilizando a biblioteca Floating UI, que calcula a posição ideal do tooltip com base no espaço disponível na tela.
   * A função `autoUpdate` é utilizada para atualizar automaticamente a posição do tooltip quando o elemento acionador é movido ou redimensionado.
   */
  private positionTooltip() {
    if (this.triggerElement == null || this.contentElement == null) return

    this.floatingManager = new FloatingUIManager({
      triggerElement: this.triggerElement,
      contentElement: this.contentElement,
      arrowElement: this.arrowElement,
      placement: this.position,
    })

    this.floatingManager.start()
  }

  private cleanupPositioning() {
    this.floatingManager?.stop()
  }

  /**
   * Define os eventos de interação do tooltip.
   * Os eventos de interação incluem mouseenter, mouseleave, focus e blur.
   * @returns void
   */
  private setupInteractions() {
    if (this.triggerElement == null || this.contentElement == null) return

    if (this.type === 'standard') {
      this.triggerElement.addEventListener('mouseenter', this.showContent)
      this.triggerElement.addEventListener('mouseleave', this.hideContent)
    }

    if (this.type === 'popover') {
      this.triggerElement.addEventListener('pointerdown', this.toggleContent)
    }

    if (this.isAutoVisible) {
      requestAnimationFrame(() => {
        this.clearShowTimer()
        this.setShowTimer()
      })
    }
  }

  /**
   * Alterna a exibição do tooltip.
   * Se o tooltip estiver visível, ele será ocultado.
   * Se o tooltip estiver oculto, ele será exibido.
   */
  private toggleContent = () => {
    this.fromPointerDown = true

    if (this.isVisible) {
      this.hideContent()
    } else {
      this.showContent()
    }
  }

  /**
   * Processa as ações relacionadas à exibição do tooltip.
   * Isso inclui exibir o tooltip e iniciar o tempo limite de ocultação automática.
   * @returns void
   */
  private showContent = () => {
    if (this.contentElement == null) return

    this.isVisible = true

    if (this.isAutoVisible) {
      if (this.hideDelay !== null) {
        this.clearHideTimer()
        this.setHideTimer()
      }
    }

    if (this.type === 'popover') {
      document.addEventListener('click', this.handleOutsideClick)
    }
  }

  /**
   * Processa as ações relacionadas ao fechamento do tooltip.
   * Isso inclui ocultar o tooltip e limpar o tempo limite de ocultação automática.
   * @returns void
   */
  private hideContent = () => {
    if (this.contentElement == null) return

    this.isVisible = false

    if (this.type === 'popover') {
      document.removeEventListener('click', this.handleOutsideClick)
      requestAnimationFrame(() => this.contentElement?.focus())
    }
  }

  /**
   * Configura o tempo limite para exibir o tooltip automaticamente.
   * O tempo limite é definido pelo valor da propriedade `showDelay`.
   */
  private setShowTimer() {
    this.showTimer = setTimeout(() => {
      this.showContent()
    }, this.showDelay || 0)
  }

  /**
   * Limpa o tempo limite de exibição automática do tooltip.
   */
  private clearShowTimer() {
    if (this.showTimer !== undefined) {
      clearTimeout(this.showTimer)
      this.showTimer = undefined
    }
  }

  /**
   * Configura o tempo limite para ocultar o tooltip automaticamente.
   * O tempo limite é definido pelo valor da propriedade `hideDelay`.
   */
  private setHideTimer() {
    this.hideTimer = setTimeout(() => {
      this.hideContent()
    }, this.hideDelay)
  }

  /**
   * Limpa o tempo limite de ocultação automática do tooltip.
   */
  private clearHideTimer() {
    if (this.hideTimer !== undefined) {
      clearTimeout(this.hideTimer)
      this.hideTimer = undefined
    }
  }

  /**
   * Lida com cliques fora do tooltip.
   * É utilizado para o tooltip do tipo 'popover'.
   * @param event MouseEvent
   */
  private handleOutsideClick = (event: MouseEvent) => {
    const path = event.composedPath()

    if (
      this.isVisible &&
      this.type === 'popover' &&
      !path.includes(this.contentElement) &&
      !path.includes(this.triggerElement)
    ) {
      this.hideContent()
    }
  }

  /**
   * Determina o mapeamento de classes CSS do componente.
   * @returns Um objeto que representa o mapeamento de classes CSS do componente.
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-tooltip': true,
    }
  }

  /**
   * Determina o mapeamento de classes CSS do trigger.
   * @returns Um objeto que representa o mapeamento de classes CSS do trigger.
   */
  private getTriggerCssClassMap(): CssClassMap {
    return {
      trigger: true,
    }
  }

  /**
   * Determina o mapeamento de classes CSS do conteúdo.
   * @returns Um objeto que representa o mapeamento de classes CSS do conteúdo.
   */
  private getContentCssClassMap(): CssClassMap {
    return {
      'is-visible': this.isVisible,
      content: true,
      popover: this.type === 'popover',
      large: this.density === 'large',
      medium: this.density === 'medium',
      small: this.density === 'small',
      info: this.state === 'info',
      warning: this.state === 'warning',
      error: this.state === 'error',
      success: this.state === 'success',
    }
  }

  /**
   * Determina o mapeamento de classes CSS da seta.
   * @returns Um objeto que representa o mapeamento de classes CSS da seta.
   */
  private getArrowCssClassMap(): CssClassMap {
    return {
      arrow: true,
      large: this.density === 'large',
      medium: this.density === 'medium',
      small: this.density === 'small',
      info: this.state === 'info',
      warning: this.state === 'warning',
      error: this.state === 'error',
      success: this.state === 'success',
    }
  }

  /**
   * Método do ciclo de vida do componente.
   * Este método é chamado uma única vez após o componente ser montado no DOM.
   */
  componentDidLoad() {
    if (typeof window === 'undefined') return
    this.setupInteractions()
  }

  /**
   * Método do ciclo de vida do componente.
   * Este método é chamado quando o componente é desconectado do DOM.
   */
  disconnectedCallback() {
    if (this.type === 'standard') {
      this.triggerElement?.removeEventListener('mouseenter', this.showContent)
      this.triggerElement?.removeEventListener('mouseleave', this.hideContent)
    } else if (this.type === 'popover') {
      this.triggerElement?.removeEventListener('pointerdown', this.toggleContent)
      document.removeEventListener('click', this.handleOutsideClick)
    }

    this.cleanupPositioning()

    this.clearShowTimer()
    this.clearHideTimer()
  }

  /**
   * Renderiza o componente.
   * Este método é responsável por gerar o conteúdo HTML do componente.
   * Ele utiliza a função `h` do Stencil para criar elementos JSX.
   * @returns JSX.Element
   */
  render() {
    return (
      <Host>
        <div id={this.customId} class={this.getCssClassMap()}>
          <div
            class={this.getTriggerCssClassMap()}
            ref={(el) => (this.triggerElement = el as HTMLDivElement)}
            aria-describedby={`${this.customId}-content`}
            aria-expanded={this.isVisible ? 'true' : 'false'}
            aria-controls={`${this.customId}-content`}
            role="button"
            tabindex="-1  "
          >
            <slot name="trigger"></slot>
          </div>
          <div
            id={`${this.customId}-content`}
            class={this.getContentCssClassMap()}
            ref={(el) => (this.contentElement = el as HTMLDivElement)}
            role={this.type === 'popover' ? 'dialog' : 'tooltip'}
            tabindex="-1"
          >
            <slot name="content"></slot>
            {this.type === 'popover' && (
              <br-button
                class="close"
                shape="circle"
                density="small"
                onClick={this.hideContent}
                aria-label="Fechar popover"
              >
                <br-icon icon-name="fa6-solid:xmark"></br-icon>
              </br-button>
            )}
            <div class={this.getArrowCssClassMap()} ref={(el) => (this.arrowElement = el as HTMLDivElement)}></div>
          </div>
        </div>
      </Host>
    )
  }
}
